/* eslint-disable no-undef */
import React from 'react'
import ReactDOM from 'react-dom/client'
import { Statistic } from 'antd'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import SettingProvider from './store/SettingProvider.js'
import AuthProvider from './store/AuthProvider.js'
import DashboardProvider from './store/DashboardProvider.js'
import TimeLineDashboardProvider from './store/TimeLineDashboardProvider.js'
import AnalysisProvider from './store/AnalysisProvider.js'
import App from './App'
import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import {
    openNotification,
    SessionClear,
    getToken,
    systemID,
} from './functionTool/functionTool'
import { GetEncryptedString, GetDecryptedString } from './functionTool/encryption'

let abortControllers = new Map()
let isLoggedOut = false
let isReloggingIn = false
const { Countdown } = Statistic

// 讀取「記住我」加密帳密（Login 以 useLocalStorage 存為 JSON）
const readRememberedCredential = () => {
    try {
        const encUser = window.localStorage.getItem('username')
        const encPass = window.localStorage.getItem('password')
        if (!encUser || !encPass) return null
        const account = GetDecryptedString(JSON.parse(encUser))
        const password = GetDecryptedString(JSON.parse(encPass))
        if (!account || !password) return null
        return { account, password }
    } catch (e) {
        return null
    }
}

// 看板自動重登：session 徹底失效時，用記住的帳密自動重新登入，避免看板中斷
const attemptAutoRelogin = async () => {
    const cred = readRememberedCredential()
    if (!cred) return false
    try {
        // 用獨立 instance 避開攔截器；withCredentials 讓後端重新種 refresh cookie
        const res = await refreshInstance.post(
            `${process.env.REACT_APP_APIGATEWAY_URL}/auth/login`,
            { systemID, account: cred.account, password: cred.password },
            { withCredentials: true }
        )
        if (res?.data?.status === true && res?.data?.data?.token) {
            const bearerToken = 'Bearer ' + res.data.data.token
            window.localStorage.setItem('token', GetEncryptedString(bearerToken))
            window.dispatchEvent(
                new CustomEvent('tokenRefreshed', { detail: { token: bearerToken } })
            )
            return true
        }
        return false
    } catch (e) {
        console.error('[自動重登] 失敗:', e)
        return false
    }
}

const logoutUser = async () => {
    if (isLoggedOut || isReloggingIn) return

    // 看板自動重登：先嘗試用「記住我」的帳密重新登入，成功則不中斷看板
    isReloggingIn = true
    const relogged = await attemptAutoRelogin()
    isReloggingIn = false
    if (relogged) {
        console.log('[自動重登] session 失效，已自動重新登入，看板繼續運作')
        return
    }

    isLoggedOut = true

    // 廢止 refresh token（best-effort，不阻塞登出流程）
    const token = getToken()
    if (token) {
        axios.post(
            `${process.env.REACT_APP_APIGATEWAY_URL}/auth/logout`,
            {},
            { headers: { Authorization: token }, withCredentials: true }
        ).catch((e) => console.error('Logout API error:', e))
    }

    openNotification(
        'top',
        <div>
            登入逾時，系統將於
            <Countdown
                value={Date.now() + 3 * 1000}
                format="s"
                onFinish={() => {
                    // 清除localStorage
                    SessionClear()
                    // 重置登出標記
                    isLoggedOut = false
                    // 跳轉到登入頁面
                    window.location.href = '/login'
                }}
                style={{ display: 'inline-block', padding: '0 2px' }}
                className="logout-count-down"
            />
            秒後自動登出，請再次登入
        </div>,
        '401'
    )

    // 使用AbortController取消所有進行中的request
    abortControllers.forEach((controller, url) => {
        controller.abort()
        abortControllers.delete(url)
    })

    // 重置 AbortController Map
    abortControllers.clear()

    // 清除 React Query 快取
    queryClient.clear()
}

// ── Refresh Token 機制 ───────────────────────────────────────────────────

const BUFFER_TIME = 60 * 60 * 1000  // 提前 1 小時刷新（Cognito Access Token 壽命 2 小時）

const refreshInstance = axios.create()  // 獨立 instance，不繼承 interceptors，避免遞迴

let isRefreshing = false
let refreshSubscribers = []

const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb)
const onTokenRefreshed = (newToken) => {
    refreshSubscribers.forEach((cb) => cb(newToken, null))
    refreshSubscribers = []
}
const onTokenRefreshFailed = (err) => {
    refreshSubscribers.forEach((cb) => cb(null, err))
    refreshSubscribers = []
}

const doRefresh = () =>
    refreshInstance
        .post(
            `${process.env.REACT_APP_APIGATEWAY_URL}/auth/refresh`,
            { systemID },
            { headers: { Authorization: getToken() }, withCredentials: true }
        )
        .then((res) => {
            const rawToken = res.data.result.token
            const bearerToken = 'Bearer ' + rawToken
            window.localStorage.setItem('token', GetEncryptedString(bearerToken))
            // CustomEvent 通知 React 更新 token state
            window.dispatchEvent(new CustomEvent('tokenRefreshed', { detail: { token: bearerToken } }))
            return bearerToken
        })

axios.interceptors.request.use(
    (config) => {
        try {
            // 只處理有 Authorization header 的請求
            const authHeader = config.headers?.Authorization
            if (!authHeader) return config

            // 以 localStorage 最新 token 覆蓋 header（refresh 後 React state 可能仍是舊值）
            const freshToken = getToken()
            if (freshToken) {
                config.headers.Authorization = freshToken
            }

            const storedToken = freshToken || authHeader
            const rawToken = storedToken.replace('Bearer', '').trim()
            if (!rawToken) return config

            const decodedToken = jwtDecode(rawToken)
            const expirationTime = (decodedToken?.exp || 1) * 1000
            const isBackground = config.isBackground ?? false

            // 已過期或即將過期（進入 BUFFER 區間）→ 先嘗試刷新，刷新失敗才登出
            // （不再「過期就直接登出」，因為 refresh token 通常仍有效，可無痛續期）
            if (Date.now() >= expirationTime - BUFFER_TIME) {
                if (isBackground) return config  // 背景請求不觸發 refresh，避免延長 Session 壽命

                if (!isRefreshing) {
                    isRefreshing = true
                    const currentToken = getToken()
                    if (!currentToken) {
                        isRefreshing = false
                        logoutUser()
                        return Promise.reject('Missing token')
                    }

                    // fire-and-forget：刷新非阻塞，所有請求（含觸發者）統一入隊等待
                    doRefresh()
                        .then((newToken) => {
                            isRefreshing = false
                            onTokenRefreshed(newToken)
                        })
                        .catch((err) => {
                            isRefreshing = false
                            console.error(`刷新 Token 失敗: ${err.response?.data?.message || err.message}`)
                            onTokenRefreshFailed(err)
                            logoutUser()
                        })
                }

                // 觸發 refresh 的請求也入隊，與其他請求一致等待新 token
                return new Promise((resolve, reject) => {
                    subscribeTokenRefresh((newToken, err) => {
                        if (err) return reject(err)
                        config.headers['Authorization'] = newToken
                        resolve(config)
                    })
                })
            }

            return config
        } catch (error) {
            return Promise.reject(error)
        }
    },
    (error) => Promise.reject(error)
)

axios.interceptors.response.use(
    (response) => {
        const errorMessage = response?.data?.errors?.[0]?.message
        if (errorMessage) {
            console.warn(errorMessage)
            
            // 檢查是否為授權相關的 GraphQL 錯誤
            if (errorMessage.includes('Not Authorized') || 
                errorMessage.includes('Unauthorized') ||
                errorMessage.includes('Token expired') ||
                errorMessage.includes('Invalid token')) {
                console.warn('GraphQL 授權錯誤，觸發登出')
                logoutUser()
                return response
            }
            
            const getTimedOutApiName = () => {
                try {
                    const body = JSON.parse(response.config.data)
                    const match = body.query?.match(/\n\s*(vpp_\w+)/)
                    return match?.[1] ?? null
                } catch {
                    return null
                }
            }
            const apiName = errorMessage.includes('timed out') ? getTimedOutApiName() : null
            const displayMessage = errorMessage.includes('timed out')
                ? `API 回應逾時，請稍後再試${apiName ? `（${apiName}）` : ''}`
                : errorMessage
            openNotification(
                'top',
                displayMessage,
                displayMessage
            )
        }
        abortControllers.delete(response.config.url)
        return response
    },
    (error) => {
        abortControllers.delete(error.config?.url)
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    console.warn('未經授權')
                    logoutUser()
                    break
                case 403:
                    console.warn('沒有存取權限')
                    openNotification(
                        'top',
                        '沒有存取權限，無權限使用此動作，如有疑問請通知系統管理員確認權限',
                        '403'
                    )
                    break
                case 404:
                    console.warn('找不到該頁面')
                    break
                case 500:
                    console.warn('伺服器出錯')
                    openNotification('top', '伺服器出錯', '500')
                    break
                case 502:
                    console.warn('伺服器出錯')
                    openNotification('top', '伺服器出錯', '503')
                    break
                case 503:
                    console.warn('服務失效')
                    openNotification('top', '服務失效', '503')
                    break
                default:
                    console.warn(`連接錯誤: ${error.response.status}`)
            }
        }
        return Promise.reject(error)
    }
)

// Create a client
const queryClient = new QueryClient()

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <SettingProvider>
                <AuthProvider>
                    <DashboardProvider>
                        <AnalysisProvider>               
                            <TimeLineDashboardProvider>
                                <App />
                            </TimeLineDashboardProvider>
                        </AnalysisProvider>
                    </DashboardProvider>
                </AuthProvider>
            </SettingProvider>
        </QueryClientProvider>
    </React.StrictMode>
)
